// apps/web/src/features/bookings/components/time-selection/StartTimeGrid.tsx
import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import type { SlotAvailability } from '@lov/shared';
import { getAvailabilityIndicator, AVAILABILITY_COLORS } from '@lov/shared';

interface StartTimeGridProps {
  slots: SlotAvailability[];
  selectedTime: string | null;
  onTimeSelect: (time: string) => void;
  maxCapacity?: number;
  totalQuantity?: number;
}

const TimeSlotButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'isSelected' &&
    prop !== 'isAvailable' &&
    prop !== 'borderColor' &&
    prop !== 'isBooked',
})<{
  isSelected: boolean;
  isAvailable: boolean;
  borderColor?: string;
  isBooked?: boolean;
}>(({ theme, isSelected, isAvailable, borderColor, isBooked }) => ({
  minWidth: 0,
  minHeight: 56, // Fixed height for uniform slot appearance
  padding: theme.spacing(1, 1.5),
  fontSize: '0.75rem', // Reduced from 0.875rem for smaller text
  fontWeight: 500,
  borderRadius: 4, // Reduced from 8 for more subtle corners
  textTransform: 'none',
  transition: 'all 0.2s',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.25),
  ...(isSelected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderLeft: `4px solid ${theme.palette.primary.dark}`, // Left accent border
    borderTop: '1px solid',
    borderRight: '1px solid',
    borderBottom: '1px solid',
    borderTopColor: theme.palette.primary.dark,
    borderRightColor: theme.palette.primary.dark,
    borderBottomColor: theme.palette.primary.dark,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(!isSelected &&
    isAvailable &&
    !isBooked && {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      // Left border accent (cinema style)
      borderLeft: borderColor ? `4px solid ${borderColor}` : `4px solid ${theme.palette.divider}`,
      borderTop: '1px solid',
      borderRight: '1px solid',
      borderBottom: '1px solid',
      borderTopColor: theme.palette.divider,
      borderRightColor: theme.palette.divider,
      borderBottomColor: theme.palette.divider,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderLeftColor: borderColor || theme.palette.primary.main,
      },
    }),
  ...(!isAvailable &&
    isBooked && {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.text.disabled,
      borderLeft: `4px solid ${AVAILABILITY_COLORS.unavailable}`, // Left accent for unavailable
      borderTop: '1px solid',
      borderRight: '1px solid',
      borderBottom: '1px solid',
      borderTopColor: theme.palette.divider,
      borderRightColor: theme.palette.divider,
      borderBottomColor: theme.palette.divider,
      cursor: 'not-allowed',
      pointerEvents: 'none',
      textDecoration: 'line-through',
    }),
}));

const AvailabilityLabel = styled(Typography)<{ color?: string }>(({ color }) => ({
  fontSize: '0.625rem',
  fontWeight: 600,
  color: color || 'inherit',
  lineHeight: 1,
}));

function formatTime(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

export function StartTimeGrid({
  slots,
  selectedTime,
  onTimeSelect,
  maxCapacity,
  totalQuantity,
}: StartTimeGridProps) {
  // Only show slots that can be start times (not the last slot)
  const startableSlots = useMemo(() => slots.slice(0, -1), [slots]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Select Start Time
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Choose when you'd like to begin your session
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(4, 1fr)',
            sm: 'repeat(6, 1fr)',
            md: 'repeat(8, 1fr)',
          },
          gap: 1,
        }}
      >
        {startableSlots.map((slot) => {
          // Calculate availability indicator
          const capacity = maxCapacity || totalQuantity || 0;
          const indicator =
            capacity > 0 ? getAvailabilityIndicator(slot.available, capacity) : null;
          const isBooked = slot.available === 0 && !slot.isAvailable;

          // Determine if we should show the label
          const showLabel = indicator && indicator.level !== 'full' && indicator.level !== 'high';

          return (
            <Tooltip
              key={slot.startTime}
              title={
                slot.isAvailable ? `${slot.available} of ${capacity} available` : 'Fully booked'
              }
              arrow
            >
              <TimeSlotButton
                isSelected={selectedTime === slot.startTime}
                isAvailable={slot.isAvailable}
                isBooked={isBooked}
                borderColor={indicator?.color}
                onClick={() => slot.isAvailable && onTimeSelect(slot.startTime)}
                disabled={!slot.isAvailable}
              >
                <time>{formatTime(slot.startTime)}</time>
                {showLabel && indicator && (
                  <AvailabilityLabel color={indicator.color}>{indicator.label}</AvailabilityLabel>
                )}
              </TimeSlotButton>
            </Tooltip>
          );
        })}
      </Box>

      {selectedTime && (
        <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
          Selected: {formatTime(selectedTime)}
        </Typography>
      )}
    </Box>
  );
}
